import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { renderToString } from 'react-dom/server';
import NavigationIcon from '@mui/icons-material/NavigationRounded';
import { WARNING } from '#action/types';
import { ReactComponent as MapFlagStartIcon } from '#static/media/map_flag_start.svg';
import { ReactComponent as MapFlagCurrentIcon } from '#static/media/map_flag_current.svg';
import { ReactComponent as MapFlagStartAndCurrentIcon } from '#static/media/map_flag_start_and_current.svg';
import ResetButton from './reset-button';
import LastLocationButton from './last-location-button';
import Locations from './locations';
import ResetClickedLocationButton from './reset-clicked-location-button';
import Routes from './routes';
import Checkpoints from './checkpoints';
import { MapContainer } from './styles';
import { isWidth, getMapCenter } from '#helper';

import './style.css';

const { VITE_MAPBOX_GLJS_API, VITE_MAPBOX_GLJS_STYLE } = import.meta.env;

function addIcons(map, icons) {
  return Promise.all(icons.map(({ name, Component, size = [24, 24], sdf = false, fill = 'none' }) =>
    new Promise(resolve => {
      const customIcon = new Image(...size);
      customIcon.onload = () => {
        map.addImage(name, customIcon, { sdf });
        resolve(true);
      };
      // customIcon.onload = () => console.log(customIcon);
      const b64 = `data:image/svg+xml;base64,${btoa(renderToString(
        <Component xmlns="http://www.w3.org/2000/svg" width={size[0]} height={size[1]} fill={fill} />
      ))}`;
      customIcon.src = b64;
    })));
}

function showSelectDeviceWarning(dispatch) {
  dispatch({
    type: WARNING,
    payload: {
      message: 'Select a device'
    }
  });
}

function updateSelectedDevices(data, destinations) {
  this.app.devices = data;
  const geojson = Locations.getGeoJSON(
    { devices: data, destinations },
    getMapCenter(this.app.account)
  );
  this.app.locationsBound = geojson.locationsBound;
  this.app.lastLocationBound = geojson.lastLocationBound;
  this.getSource('locations').setData(geojson.data);
  this.getSource('routes').setData(Routes.getGeoJSON(data).data);
}

function initMap(container, data, account, meta, history, theme, helper, options) {
  const { mapboxgl } = window;
  if (!mapboxgl) return null;

  const { locationsBound, lastLocationBound, devices, ...geojson } = data.geojson;

  mapboxgl.accessToken = VITE_MAPBOX_GLJS_API;
  const noMapPoints = options.noLocations && !data?.destinations?.some(d => d?.locations?.length);

  const map = new mapboxgl.Map({
    container,
    style: VITE_MAPBOX_GLJS_STYLE,
    bounds: locationsBound,
    fitBoundsOptions: {
      padding: 50,
      ...(noMapPoints ? { zoom: 1 } : { maxZoom: 10 })
    },
    maxZoom: 17,
    showZoom: true,
    preserveDrawingBuffer: options.exportPage
  });
  map.app = {
    account,
    meta,
    history,
    theme,
    helper,
    devices,
    destinations: data.destinations,
    highlightCheckpoint: Checkpoints.highlight.bind(Checkpoints),
    clickCheckpoint: Checkpoints.click.bind(Checkpoints),
    options,
    updateSelectedDevices: updateSelectedDevices.bind(map),
    locationsBound,
    lastLocationBound,
    clickedLocations: []
  };

  const nav = new mapboxgl.NavigationControl({ showCompass: false });
  map.addControl(nav, 'top-right');

  // disable map rotation using right click + drag
  map.dragRotate.disable();

  // disable map rotation using touch rotation gesture
  map.touchZoomRotate.disableRotation();

  map.on('load', async () => {
    await addIcons(map, [
      { name: 'navigation', Component: NavigationIcon, sdf: true, fill: '#FFF' },
      { name: 'map-flag-start', Component: MapFlagStartIcon, size: [67, 66] },
      { name: 'map-flag-current', Component: MapFlagCurrentIcon, size: [67, 66] },
      { name: 'map-flag-start-and-current', Component: MapFlagStartAndCurrentIcon, size: [67, 66] }
    ]);

    if (options.zoomLastLocation && lastLocationBound) {
      map.fitBounds(lastLocationBound, { padding: 50, maxZoom: 15 });
    }

    /* const drawClusters = Locations.memoDrawClusters(map);
    map.on('render', () => {
      if (!map.isSourceLoaded('locations')) return;
      drawClusters();
    }); */

    // add routes
    map.addSource('routes', Routes.getGeoJSON(devices));
    Routes.addLayer(map);

    map.addSource('locations', Locations.addClusterProperties(geojson, devices));
    Locations.addLayer(map);

    Locations.addPlaceHover();
    Locations.addPlaceClick();
    Locations.addClusterHover();
    Locations.addClusterClick();

    if (data.destinations) {
      // puts `destinations` above routes
      map.addSource('destinations', Checkpoints.getGeoJSON(data.destinations));
      Checkpoints.addLayer(map);
      Checkpoints.addHover();
    }

    Locations.addFlagLayer(map);

    map.addControl(new ResetButton(() => {
      if (map.app.devices.length) {
        map.fitBounds(map.app.locationsBound, {
          padding: 50,
          ...(noMapPoints ? { zoom: 1 } : { maxZoom: 10 })
        });
      } else {
        showSelectDeviceWarning(helper.dispatch);
      }
    }));

    if (!options.noLocations) {
      map.addControl(new LastLocationButton(() => {
        if (map.app.devices.length && map.app.lastLocationBound) {
          map.fitBounds(map.app.lastLocationBound, { padding: 50, maxZoom: 15 });
        } else {
          showSelectDeviceWarning(helper.dispatch);
        }
      }));

      map.app.resetClickedLocationButton =
        new ResetClickedLocationButton(() => {
          map.app.clickedLocations.forEach(featureId => {
            map.setFeatureState(
              { source: 'locations', id: featureId },
              { clicked: false }
            );
          });
          map.app.clickedLocations = [];
          map.removeControl(map.app.resetClickedLocationButton);
        });
    }

    helper.setMap(map);
  });

  return map;
}

const Map = props => {
  const {
    loading,
    devices,
    meta,
    account,
    clickDevices,
    zoomLastLocation,
    destinations,
    setMap,
    mapClass = '',
    dispatch,
    locations = [],
    isMetricsLoaded
  } = props;

  const theme = useTheme();
  const history = useHistory();
  const style = useMemo(() => ({ height: '100%', minHeight: 275 }), []);

  const containerRef = useRef(null);
  // const [clickedDevices, setClickedDevices] = useState([])
  const map = useRef(null);
  const geojson = useMemo(
    () =>
      (!loading && account ?
        Locations.getGeoJSON({ devices, destinations }, getMapCenter(account)) : null),
    [devices, loading, destinations, account]
  );
  // console.log(devices)
  useEffect(() => {
    if (meta?.device && geojson && !map.current && containerRef.current) {
      map.current = initMap(
        containerRef.current,
        { geojson, destinations },
        account,
        meta,
        history,
        theme,
        { setClickedDevices: clickDevices, dispatch, setMap },
        { zoomLastLocation, exportPage: mapClass === 'exportPage', noLocations: Boolean(!locations?.length) }
      );
      if (map.current) {
        setTimeout(() => {
          map.current?.resize();
        }, 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.device, Boolean(geojson)]);

  useEffect(() => {
    if (map.current) {
      map.current.app.devices = devices;
      if (isMetricsLoaded && !map.current.app.allMetricsLoaded && map.current.getSource('locations')) {
        updateSelectedDevices.call(map.current, devices, destinations);
        map.current.app.allMetricsLoaded = true;
      }
    }
  }, [devices, destinations, isMetricsLoaded]);

  useEffect(() => () => {
    if (map.current) {
      try {
        map.current.remove();
      } catch (e) {
        // ignore cleanup errors
      }
      map.current = null;
    }
  }, []);
  return (
    <MapContainer className={`${isWidth(1200) ? 'mob' : ''} ${mapClass}`} elevation={0}>
      <div id="map" ref={containerRef} style={style} />
    </MapContainer>
  );
};

export default Map;
